import cn from "classnames";
import React, { useCallback, useMemo, useState } from "react";

import PopoverOverlay from "components/UI/_TODO/PopoverOverlay/PopoverOverlay";

import DropdownArrow from "shared/ui/atoms/DropdownArrow/DropdownArrow";
import ButtonBase from "shared/ui/controls/ButtonBase";
import Avatar from "shared/ui/dataDisplay/Avatar/Avatar";

import { stopEventPropagation } from "utils/helpers/stopEventPropagation";

import blackApproveIcon from "images/icons/blackCheckIcon";
import blueApproveIcon from "images/icons/blueCheckIcon";
import fillBlueApproveIcon from "images/icons/filledBlueCheckIcon";

import styles from "./ApprovingCell.module.scss";

export interface IApprovalStep {
  id: number;
  approver: {
    id: number;
    full_name: string;
  };
  status: "pending" | "approved" | "rejected";
  comment: string;
}

interface IProps {
  currentUserId: number;
  approve: () => void;
  approvals: IApprovalStep[];
  /** Ленивая загрузка согласующих, если нужно */
  onPopupOpen?: () => void;
  isPending?: boolean;
}
// TODO использовать в src/components/pages/Journal/components/JournalDelivery/components/JournalDeliveryRow/components/ApprovingCell
const ApprovingCell: React.FC<IProps> = ({ currentUserId, approve, approvals, onPopupOpen, isPending }) => {
  const [popoverIsOpen, setPopoverIsOpen] = useState(false);

  const { confirmedApprovalsCount, rejectedApprovalsCount, isCurrentUserApprove } = useMemo(() => {
    let confirmedApprovalsCount = 0;
    let rejectedApprovalsCount = 0;
    let isCurrentUserApprove = false;
    approvals?.forEach((appr) => {
      if (appr.status === "rejected") rejectedApprovalsCount++;
      if (appr.status === "approved") confirmedApprovalsCount++;
      if (appr.approver.id === currentUserId && appr.status === "approved") isCurrentUserApprove = true;
    });
    return { confirmedApprovalsCount, rejectedApprovalsCount, isCurrentUserApprove };
  }, [approvals]);

  const handlePopover = useCallback((isOpen: boolean) => {
    setPopoverIsOpen(isOpen);
  }, []);

  const hasWaitingApprovals = confirmedApprovalsCount < approvals?.length!;
  const hasExistingApprovals =
    confirmedApprovalsCount + rejectedApprovalsCount !== 0 &&
    !(confirmedApprovalsCount + rejectedApprovalsCount === 1 && isCurrentUserApprove);

  const getIcon = () => {
    if (approvals.length === confirmedApprovalsCount) return fillBlueApproveIcon;
    if (!isCurrentUserApprove) return blackApproveIcon;
    if (isCurrentUserApprove) return blueApproveIcon;
  };

  const onApprove = useCallback(() => {
    if (isCurrentUserApprove) return;
    approve();
  }, [approve, isCurrentUserApprove]);

  return (
    <div className={styles.approving} onClick={stopEventPropagation}>
      <ButtonBase
        className={cn(styles.icon, { [styles.userApproveButton]: !isCurrentUserApprove })}
        onClick={onApprove}
        isLoading={isPending}
        ghost
      >
        {getIcon()}
      </ButtonBase>
      <PopoverOverlay
        isOpen={popoverIsOpen}
        content={
          <div className={styles.popup}>
            <header className={styles.popupHeader}>
              <h6 className={styles.popupTitle}>Согласования</h6>
              {isCurrentUserApprove && (
                <div className={styles.popupYourApprove}>
                  Вы согласовали
                  {fillBlueApproveIcon}
                </div>
              )}
            </header>
            {(hasExistingApprovals || hasWaitingApprovals) && (
              <div className={styles.popupBody}>
                {hasExistingApprovals && (
                  <div>
                    <span className={styles.approvalsTitle}>
                      Согласовано: <span className={styles.approvalsCount}>{confirmedApprovalsCount}</span>
                    </span>
                    <div className={styles.approversList}>
                      {approvals
                        ?.filter((appr) => appr.status === "approved" && appr.approver.id !== currentUserId)
                        ?.map((appr) => (
                          <div className={styles.approver} key={appr.id}>
                            <div>
                              <Avatar className={styles.avatar} person={appr.approver.full_name} />
                              <span>{appr.approver.full_name}</span>
                            </div>

                            {fillBlueApproveIcon}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                {hasWaitingApprovals && (
                  <div>
                    <span className={styles.approvalsTitle}>
                      Ожидание согласования:{" "}
                      <span className={styles.approvalsCount}>{approvals?.length! - confirmedApprovalsCount}</span>
                    </span>
                    <div className={styles.approversList}>
                      {approvals
                        ?.filter((appr) => appr.status !== "approved")
                        ?.map((appr) => (
                          <div className={styles.approver} key={appr.id}>
                            <div>
                              <Avatar className={styles.avatar} person={appr.approver.full_name} />
                              <span>{appr.approver.full_name}</span>
                            </div>
                            {blueApproveIcon}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        }
        openType="click"
        popoverBorderColor="default"
        placement="bottom"
        onInteraction={handlePopover}
        onOpen={onPopupOpen}
      >
        <div className={styles.countContainer}>
          <span className={styles.count}>{`${confirmedApprovalsCount}/${approvals.length}`}</span>
          <DropdownArrow isOpen={popoverIsOpen} withBackground />
        </div>
      </PopoverOverlay>
    </div>
  );
};

export default React.memo(ApprovingCell);
